import { useEffect, useState } from "react";
import axios from "axios";
import { History } from "lucide-react";
import VehicleCard from "@/components/VehicleCard";
import useRecentlyViewed from "@/lib/useRecentlyViewed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Visti di recente" — public homepage section.
 * Reads recent vehicle IDs from localStorage, fetches matching vehicles,
 * renders nothing if user has no history (clean first-visit).
 *
 * Excludes a single optional vehicle id (e.g., current detail page).
 */
export default function RecentlyViewed({ excludeId }) {
  const { ids } = useRecentlyViewed();
  const [vehicles, setVehicles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const wanted = ids.filter((id) => id !== excludeId);
    if (!wanted.length) {
      setVehicles([]);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/vehicles`)
      .then(({ data }) => {
        if (cancelled) return;
        const byId = new Map(data.map((v) => [v.id, v]));
        const ordered = wanted.map((id) => byId.get(id)).filter(Boolean);
        setVehicles(ordered);
      })
      .catch(() => !cancelled && setVehicles([]))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [ids, excludeId]);

  if (!loaded || vehicles.length === 0) return null;

  return (
    <section
      data-testid="recently-viewed-section"
      className="relative py-16 md:py-20 bg-[#f5f6f4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-3">
              <History size={12} />
              <span>Continua dove eri rimasto</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1c1f1a] leading-tight">
              Visti di recente
            </h2>
          </div>
          <div className="hidden md:block text-xs text-[#7a7f77] font-medium">
            {vehicles.length} veicol{vehicles.length === 1 ? "o" : "i"}
          </div>
        </div>

        {/* Mobile: horizontal scroll. Desktop: responsive grid */}
        <div
          data-testid="recently-viewed-list"
          className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 pb-2"
          style={{ scrollbarWidth: "none" }}
        >
          {vehicles.map((v) => (
            <div
              key={v.id}
              className="snap-start flex-shrink-0 w-[78%] sm:w-[60%] md:w-auto"
            >
              <VehicleCard vehicle={v} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
